import assign from 'object-assign';
import { addDays, compare, OwaDate, getDate, getTimestamp, startOfDay } from 'owa-datetime';
import { CalendarEvent, MultiDayEventInstanceType } from 'mini-calendar-event-types';

/**
 * When the event start and end is different && when the end is not the begin of next day.
 * Usually happens for an all day event where the end will be 12:00 AM of next day.
 * @param event - Event which needs to be checked if it can be expanded.
 * @returns - Expanded list of events.
 */
export default function expandCalendarEventIfSpilling(event: CalendarEvent): CalendarEvent[] {
    if (
        getDate(event.Start) !== getDate(event.End) &&
        !(compare(event.End, startOfDay(addDays(event.Start, 1))) === 0)
    ) {
        const events: CalendarEvent[] = [];
        let currentStart: OwaDate = event.Start;
        let endDate = addDays(startOfDay(currentStart), 1);
        let instance = 1;
        while (endDate < event.End) {
            events.push(createExpandedEvent(event, currentStart, endDate, instance++));
            currentStart = endDate;
            endDate = addDays(endDate, 1);
        }
        events.push(createExpandedEvent(event, currentStart, event.End, instance));
        events.forEach((ev: CalendarEvent, index: number) => {
            const { MultiDayInfo, IsEffectiveAllDayEvent } = ev;
            MultiDayInfo.TotalEventInstances = instance;
            if (!IsEffectiveAllDayEvent) {
                // We need to treat only the events which are not already not all day
                // except for the tail and the head of the event, all the other instances are to be marked as all day events
                let instanceType: MultiDayEventInstanceType;
                switch (index) {
                    case 0:
                        instanceType = MultiDayEventInstanceType.HEAD;
                        break;
                    case instance - 1:
                        instanceType = MultiDayEventInstanceType.TAIL;
                        break;
                    default:
                        instanceType = MultiDayEventInstanceType.MIDDLE;
                        break;
                }
                MultiDayInfo.InstanceType = instanceType;
            }
        });
        return events;
    } else {
        return [event];
    }
}

/**
 * Creates a expanded event.
 */
function createExpandedEvent(
    event: CalendarEvent,
    start: OwaDate,
    end: OwaDate,
    instance: number
): CalendarEvent {
    return assign(new CalendarEvent(), event, {
        Start: start,
        End: end,
        Key: {
            timestamp: getTimestamp(start),
            itemId: event.ItemId.Id,
        },
        MultiDayInfo: {
            MultiDayEventInstance: instance,
            OriginalEventStart: event.Start,
            OriginalEventEnd: event.End,
            TotalEventInstances: 0,
            InstanceType: MultiDayEventInstanceType.NONE,
        },
    });
}
